import { useAppDispatch } from '@/store';
import { crmApi } from '@/store/features/crm.feature';
import { useEffect, useRef } from 'react';
import { updatePendingRefreshes } from './attio-connection-refreshing';

/** At most one refresh a minute, however often the tab is shown. */
const REFRESH_INTERVAL_MS = 60_000;

/** Reads the CRM options again when the tab is shown, so an Attio reconnect
 *  in another tab lands here. Mounted app-wide (app-effects). */
export function useAttioConnectionRefresh(organizationId: string) {
  const dispatch = useAppDispatch();
  const activeOrganizationIdRef = useRef(organizationId);
  activeOrganizationIdRef.current = organizationId;

  useEffect(() => {
    if (!organizationId) return;
    let last = -Infinity;

    const refresh = () => {
      if (
        activeOrganizationIdRef.current !== organizationId ||
        document.visibilityState !== 'visible' ||
        Date.now() - last < REFRESH_INTERVAL_MS
      ) {
        return;
      }
      last = Date.now();
      dispatch((queryDispatch, getState) => {
        const state = getState();
        const cachedArgs = crmApi.util.selectCachedArgsForQuery(
          state,
          'getCrmOptions'
        );
        for (const args of cachedArgs) {
          if (args.organizationId !== organizationId) continue;
          const { data } = crmApi.endpoints.getCrmOptions.select(args)(state);
          if (!data?.some((option) => option.type === 'attio')) continue;
          // OAuth can reconnect to another workspace in a separate tab.
          updatePendingRefreshes(organizationId, 1);
          void (async () => {
            try {
              const pending = queryDispatch(
                crmApi.util.getRunningQueryThunk('getCrmOptions', args)
              );
              // forceRefetch still deduplicates against a pre-focus request.
              if (pending) await pending;
              await queryDispatch(
                crmApi.endpoints.getCrmOptions.initiate(args, {
                  subscribe: false,
                  forceRefetch: true,
                })
              );
            } finally {
              updatePendingRefreshes(organizationId, -1);
            }
          })();
        }
      });
    };

    // Showing a tab fires visibilitychange; focus would refresh it twice.
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [dispatch, organizationId]);
}
